import { useLazyGetModulesWithAgendasQuery } from 'store/services/core/modules';
import { memo, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, SearchOff } from '@mui/icons-material';
import { CircularProgress, debounce, InputAdornment, TextField } from '@mui/material';
import { NoOptionsBox, StyledAutocomplete, StyledGroupHeader, StyledSearchOption } from './SearchBar.styles';
import { useTranslation } from 'react-i18next';

import { DATA_STRUCTURE } from 'types/enums';
import { toTestIdSegment } from 'utils/testId';
import { resolveAgendaName } from 'utils/resolveAgendaName';

interface SearchOption {
  type: 'module' | 'agenda';
  moduleId: string;
  moduleName: string;
  moduleIcon: string;
  id: string;
  name: string;
  systemType?: string;
  isChild?: boolean;
  parentModule?: {
    moduleId: string;
    moduleName: string;
    moduleIcon: string;
  };
}

export const SearchBar = memo(() => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SearchOption | null>(null);
  const [open, setOpen] = useState(false);

  const pathname = useLocation().pathname;

  const [getModulesWithAgendasQuery, { data, isFetching, error }] = useLazyGetModulesWithAgendasQuery();

  useEffect(() => {
    if (data?.modules) {
      const newOptions: SearchOption[] = [];

      data.modules.forEach((module) => {
        module.agendas.forEach((agenda) => {
          newOptions.push({
            type: 'agenda',
            moduleId: module.moduleId,
            moduleName: module.name,
            moduleIcon: module.icon,
            id: agenda.agendaId,
            name: resolveAgendaName(agenda),
            systemType: agenda.systemType,
            isChild: false,
          });
        });

        if (module.childs) {
          module.childs.forEach((childModule) => {
            childModule.agendas.forEach((agenda) => {
              newOptions.push({
                type: 'agenda',
                moduleId: childModule.moduleId,
                moduleName: childModule.name,
                moduleIcon: childModule.icon,
                id: agenda.agendaId,
                name: resolveAgendaName(agenda),
                systemType: agenda.systemType,
                isChild: true,
                parentModule: {
                  moduleId: module.moduleId,
                  moduleName: module.name,
                  moduleIcon: module.icon,
                },
              });
            });
          });
        }
      });

      setOptions(newOptions);
    }
  }, [data]);

  const debouncedFetch = useMemo(
    () =>
      debounce((searchValue: string) => {
        if (searchValue.length >= 2) {
          getModulesWithAgendasQuery({
            takeAgendas: 0,
            takeModules: 0,
            dataStructure: DATA_STRUCTURE.HIERARCHICAL,
            search: searchValue,
          });
        } else {
          setOptions([]);
        }
      }, 300),
    [getModulesWithAgendasQuery]
  );

  const renderOption = (_props: React.HTMLAttributes<HTMLLIElement>, option: SearchOption) => (
    <StyledSearchOption
      variant="text"
      href={`/user/modules/${option.moduleId}/${option.systemType}/${option.id}`}
      data-testid={`user-dashboard-search-result-${toTestIdSegment(option.moduleId)}-${toTestIdSegment(option.id)}`}>
      {option.name}
    </StyledSearchOption>
  );

  return (
    <>
      {!pathname.includes('admin') && (
        <StyledAutocomplete
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            setInputValue(newInputValue);
            if (reason === 'clear') setSelectedOption(null);
            if (newInputValue.length >= 2) {
              debouncedFetch(newInputValue);
            } else {
              setOptions([]);
            }
          }}
          options={options}
          getOptionLabel={(option) => option.name}
          loading={isFetching}
          fullWidth
          popupIcon={null}
          renderOption={renderOption}
          groupBy={(option) => (option.isChild ? `${option.parentModule?.moduleName}|${option.moduleName}` : option.moduleName)}
          renderGroup={(params) => (
            <li key={params.key}>
              <StyledGroupHeader>
                {params.group.includes('|') ? (
                  <>
                    {params.group.split('|')[0]}
                    <span className="child-module">{params.group.split('|')[1]}</span>
                  </>
                ) : (
                  params.group
                )}
              </StyledGroupHeader>
              <ul>{params.children}</ul>
            </li>
          )}
          filterOptions={(x) => x}
          noOptionsText={
            <NoOptionsBox>
              <SearchOff />
              {t('search.noResults')}
            </NoOptionsBox>
          }
          value={selectedOption}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('common.search._')}
              variant="outlined"
              size="small"
              error={!!error}
              helperText={error ? t('search.failed') : ''}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', ml: 0.5, fontSize: '20px' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {isFetching && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                'data-testid': 'app-global-search-field',
              }}
            />
          )}
        />
      )}
    </>
  );
});
